import { v4 as uuidV4 } from 'uuid';
import { noop } from 'lodash';

import * as clientStorage from './clientStorage';
import * as jsonApi from './jsonApi';
import * as subscriptions from './subscriptions';
import { del, get, getWithHeaders, patch, post, put } from './api/apiCore';
import { CORVA_API_URLS } from './constants';
import * as corvaUrls from './corvaUrls';
import { handleSubscriptionsVersion } from './subscriptions/constants';
import { socketClientV2 } from './subscriptions/socket-client.v2';

export { clientStorage, jsonApi, subscriptions, corvaUrls };
export * as sso from './sso';
export * from './utils';

export const corvaAPI = {
  get: (path, queryParams = {}, requestOptions = {}) => {
    return get(path, queryParams, requestOptions);
  },
  getWithHeaders: (path, queryParams = {}, requestOptions = {}) => {
    return getWithHeaders(path, queryParams, requestOptions);
  },
  put: (path, content, queryParams = {}, requestOptions = {}) => {
    return put(path, content, queryParams, requestOptions);
  },
  patch: (path, content, queryParams = {}, requestOptions = {}) => {
    return patch(path, content, queryParams, requestOptions);
  },
  post: (path, entity, requestOptions = {}) => {
    return post(path, entity, requestOptions);
  },
  del: (path, queryParams = {}, requestOptions = {}) => {
    return del(path, queryParams, requestOptions);
  },
};

export const corvaDataAPI = {
  get: (path, queryParams = {}, requestOptions = {}) =>
    get(path, queryParams, { apiUrl: CORVA_API_URLS.DATA_API, ...requestOptions }),
  put: (path, content, queryParams = {}, requestOptions = {}) =>
    put(path, content, queryParams, { apiUrl: CORVA_API_URLS.DATA_API, ...requestOptions }),
  patch: (path, content, queryParams = {}, requestOptions = {}) =>
    patch(path, content, queryParams, { apiUrl: CORVA_API_URLS.DATA_API, ...requestOptions }),
  post: (path, entity, requestOptions = {}) =>
    post(path, entity, { apiUrl: CORVA_API_URLS.DATA_API, ...requestOptions }),
  del: (path, queryParams = {}, requestOptions = {}) =>
    del(path, queryParams, { apiUrl: CORVA_API_URLS.DATA_API, ...requestOptions }),
};

const SOCKET_NOT_INITIALIZED_ERROR = 'Socket is not initialized!';

function getAppInstanceId() {
  if (window.xprops) {
    const appId = window.xprops?.app?.app?.id || 'unknown-app-id';
    const appInstanceId = window.xprops?.app?.id || 'unknown-instance-id';

    return `${appId}:${appInstanceId}:${uuidV4()}`;
  }

  return `non-dc-app:${uuidV4()}`;
}

const socketClientV1 = {
  subscribe: (subscription, { onDataReceive } = {}) => {
    const { subscribe, unsubscribe, socket } = window[Symbol.for('socket')] || {};

    if (!socket) {
      console.error(SOCKET_NOT_INITIALIZED_ERROR);
      // In some environments, like PDF Dashboard Report, socket is not initialized
      // But socketClient should not throw errors to prevent apps crashing
      return noop;
    }
    const subId = getAppInstanceId();
    const subscriptionWithId = {
      ...subscription,
      // NOTE: Add ability to pass dataset (because we don't use "collection" in new data api)
      collection: subscription.collection || subscription.dataset,
      // NOTE: Unique id is required for subscriptions server
      appInstanceId: subId,
    };

    const onData =
      onDataReceive && (event => event.appInstanceId === subId && onDataReceive(event));

    subscribe(subscriptionWithId);
    if (onData) socket.on('data', onData);

    // NOTE: Return unsubscribe
    return () => {
      if (onData) socket.off('data', onData);
      unsubscribe(subscriptionWithId);
    };
  },
  onConnectionChange: handler => {
    const { socket } = window[Symbol.for('socket')] || {};
    if (!socket) return () => {};
    const onConnect = () => handler({ status: 'connected' });
    const onDisconnect = reason => handler({ status: 'disconnected', reason });
    const onReconnect = () => handler({ status: 'reconnecting' });
    const onConnectError = err => handler({ status: 'error', reason: err?.message || String(err) });

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('reconnect', onReconnect);
    socket.on('connect_error', onConnectError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('reconnect', onReconnect);
      socket.off('connect_error', onConnectError);
    };
  },
};

export const socketClient = handleSubscriptionsVersion(socketClientV1, socketClientV2);

export { apiRequestsRateLimiter } from './api/rateLimiter';
